import { getBrowserUsed } from '@packages/common/utils/getBrowserUsed';
import { serviceWorkerBroadcastChannel } from '../constants';
/// <reference lib="webworker" />
import type {
	ServiceWorkerInputMessage,
	ServiceWorkerOutputMessage,
} from '../types';

export interface Channel {
	addEventListener(
		type: string,
		listener: (event: MessageEvent<ServiceWorkerOutputMessage>) => void,
		options?: boolean | AddEventListenerOptions,
	): void;

	postMessage(message: ServiceWorkerInputMessage): void;
}
interface CustomExtendableMessageEvent<TypeMessage>
	extends ExtendableMessageEvent {
	data: TypeMessage;
}

interface CustomServiceWorkerGlobalScopeEventMap
	extends ServiceWorkerGlobalScopeEventMap {
	message: CustomExtendableMessageEvent<ServiceWorkerInputMessage>;
}

export interface ServiceWorkerChannel {
	addEventListener<K extends keyof CustomServiceWorkerGlobalScopeEventMap>(
		type: K,
		listener: (event: CustomServiceWorkerGlobalScopeEventMap[K]) => void,
		options?: boolean | AddEventListenerOptions,
	): void;

	postMessage(message: ServiceWorkerOutputMessage): void;
}

export function getChannel(): Channel {
	const browserUsed = getBrowserUsed();

	if (
		!(
			browserUsed.name === 'Safari' &&
			browserUsed.version.match(/^(10|11|12|13|14|15)/)
		)
	)
		return new window.BroadcastChannel(serviceWorkerBroadcastChannel);
	return {
		// biome-ignore lint/suspicious/noExplicitAny: <explanation>
		postMessage: (message: any) => {
			navigator.serviceWorker.controller?.postMessage(message);
		},
		addEventListener(
			_type: string,
			listener: (event: MessageEvent) => void,
		) {
			if ('serviceWorker' in navigator) {
				navigator.serviceWorker.ready.then(() => {
					navigator.serviceWorker.onmessage = listener;
				});
			}
		},
	};
}

export function getServiceWorkerChannel(
	serviceWorkerGlobalScope: ServiceWorkerGlobalScope,
): ServiceWorkerChannel {
	const browserUsed = getBrowserUsed();

	if (
		!(
			browserUsed.name === 'Safari' &&
			browserUsed.version.match(/^(10|11|12|13|14|15)/)
		)
	)
		return new BroadcastChannel(serviceWorkerBroadcastChannel);
	return {
		// biome-ignore lint/suspicious/noExplicitAny: <explanation>
		postMessage: (message: any) => {
			serviceWorkerGlobalScope.clients.matchAll().then((clientList) => {
				if (clientList?.length) {
					for (const client of clientList) {
						if (client.url.includes(location.host)) {
							client.postMessage(message);
						}
					}
				}
			});
		},
		addEventListener<
			K extends keyof CustomServiceWorkerGlobalScopeEventMap,
		>(
			type: K,
			listener: (
				event: CustomServiceWorkerGlobalScopeEventMap[K],
			) => void,
		) {
			serviceWorkerGlobalScope.addEventListener(type, listener);
		},
	};
}
export type { ServiceWorkerInputMessage, ServiceWorkerOutputMessage };
